import { forwardRef } from 'react'
import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'
import { Loader2 } from 'lucide-react'
import { cn } from '../../lib/cn.js'

/**
 * Every button in the product.
 *
 * **One action colour.** The brand notes are explicit: Nectarine is the action,
 * everywhere, and a second action colour is a second thing to learn. So `primary`
 * is the only filled coral button; `secondary` is the same shape with a hairline;
 * `tertiary` is the one that carries no weight at all.
 *
 * `destructive` exists because removing somebody's access and saving a form must
 * not look identical — but it is the error ink, not a second brand colour.
 */
const button = cva(
  [
    'inline-flex items-center justify-center gap-2 whitespace-nowrap',
    'font-medium rounded-md border transition-colors',
    'outline-none focus-visible:ring-2 focus-visible:ring-coral-fill focus-visible:ring-offset-2',
    'focus-visible:ring-offset-canvas',
    'disabled:opacity-50 disabled:pointer-events-none',
    '[&_svg]:size-4 [&_svg]:shrink-0',
  ],
  {
    variants: {
      variant: {
        primary: 'bg-coral-fill text-on-coral border-transparent hover:bg-coral-fill-hover',
        secondary: 'bg-surface text-ink border-hairline-strong hover:bg-sunk',
        tertiary: 'bg-transparent text-coral-text border-transparent hover:bg-coral-soft',
        ghost: 'bg-transparent text-ink-2 border-transparent hover:bg-sunk',
        destructive: 'bg-error-fill text-on-error border-transparent hover:opacity-90',
      },
      size: {
        /*
          shadcn's scale, which is the right one for a desk.

          These were the kit's — `--fawn-control-sm: 44px`, `--md: 48px` — and
          taking them was a mistake I made reading `fawn.css` as though it
          described this product. It does not: 44 and 48 are **touch targets**,
          the sizes a thumb needs, and `fawn.css` is a phone design system that
          the admin desk happens to share colours with. A 48px button under a
          mouse on a 1440px screen is a button that has been shouted.

          shadcn's own scale then went too far the other way: its current
          default is 36 and that read as *thin* on this desk, which is dense
          with tables and filter rows. The classic shadcn height — 40 — is the
          one that sits right between a phone's thumb and a spreadsheet's row.

          40 default, 36 small, 44 large, 40 square for an icon, 36 for the
          small square a table row uses.
        */
        sm: 'h-9 px-3 text-second',
        md: 'h-10 px-4 text-body',
        lg: 'h-11 px-6 text-body',
        /** A square for an icon alone. Always give it an `aria-label`. */
        icon: 'h-10 w-10 p-0',
        'icon-sm': 'h-9 w-9 p-0',
      },
      /** Removes the horizontal padding, for a button that sits inline in a sentence. */
      /**
       * Inline in a sentence, so no horizontal padding and no fixed height.
       *
       * `py-1.5 -my-1.5` all the same: a flush button is a line of text and
       * was 22px tall, which is a poor thing to aim a thumb at. The padding
       * grows the target and the negative margin gives the space back, so the
       * sentence around it does not move a pixel.
       */
      flush: { true: 'px-0 h-auto py-1.5 -my-1.5', false: '' },
      full: { true: 'w-full', false: '' },
    },
    defaultVariants: { variant: 'primary', size: 'md', flush: false, full: false },
  },
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof button> {
  /** Render as the child element — a link that should look like a button. */
  asChild?: boolean
  /**
   * Shows a spinner and blocks the press. The label stays, because a button whose
   * text is replaced by "Loading" is a button that changes width mid-press.
   */
  loading?: boolean
  /**
   * Icon only on a phone, icon and words from `sm` up.
   *
   * The buttons in a page heading — Export, Add, Invite — are an icon and one
   * word each. Three of them plus a search box is more than 390px holds, and
   * the icon is the half somebody recognises. Asked for directly: *"the
   * button of page header can be just icon only for mobile"*.
   *
   * The word is **hidden, not removed**: `sr-only` keeps it as the button's
   * accessible name, so an icon-only button still announces itself as
   * "Export" rather than as nothing.
   */
  compact?: boolean
  leadingIcon?: React.ReactNode
  trailingIcon?: React.ReactNode
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    className,
    variant,
    size,
    flush,
    full,
    compact,
    asChild,
    loading,
    leadingIcon,
    trailingIcon,
    children,
    disabled,
    ...props
  },
  ref,
) {
  const Comp = asChild ? Slot : 'button'
  return (
    <Comp
      ref={ref}
      className={cn(
        button({ variant, size, flush, full }),
        /* While the label is hidden the button is a square, not a text button
           with nothing in it: `px-4` on each side of a 16px icon made it 50px
           wide, and two of those took a hundred pixels from the search box
           beside them for padding around nothing. */
        compact && 'w-10 justify-center px-0 sm:w-auto sm:justify-center sm:px-4',
        className,
      )}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      /**
       * A button is not a submit button unless it says so.
       *
       * HTML's default is `submit`, which is only ever right for one button
       * on a page and is wrong for the Cancel beside it. Now that the sheets
       * are real forms, that default would have turned every Cancel, every
       * ✕ and every Remove inside one into a save. `{...props}` follows, so
       * a caller can still ask for `type="submit"`.
       */
      {...(asChild ? {} : { type: 'button' as const })}
      {...props}
    >
      {loading ? <Loader2 className="animate-spin" aria-hidden /> : leadingIcon}
      {compact ? <span className="sr-only sm:not-sr-only">{children}</span> : children}
      {!loading && trailingIcon}
    </Comp>
  )
})
